import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Tutte le rotte backend sono sotto /api
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

export const BACKEND = BACKEND_URL;

export const TOKEN_KEY = "crm_token";

// Aggiunge il token Bearer a ogni richiesta
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ---------------- Cache locale + resilienza di rete ----------------
// Obiettivo: niente pagine vuote. Le GET vengono salvate in cache (memoria +
// sessionStorage): tornando su una pagina i dati riappaiono subito, e se la
// connessione cade per un attimo si riprova e si mostrano gli ultimi dati noti.
const CACHE_PREFIX = "crm_cache:";
const FRESH_MS = 60000; // entro 60s la cache è servita subito (con revalidazione in background)
const MAX_ENTRY = 400000; // non salvare in sessionStorage payload enormi
const mem = new Map();

function cacheGet(key) {
  if (mem.has(key)) return mem.get(key);
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (raw) {
      const v = JSON.parse(raw);
      mem.set(key, v);
      return v;
    }
  } catch { /* storage non disponibile */ }
  return null;
}

function cacheSet(key, data) {
  const v = { t: Date.now(), data };
  mem.set(key, v);
  try {
    const s = JSON.stringify(v);
    if (s.length <= MAX_ENTRY) sessionStorage.setItem(CACHE_PREFIX + key, s);
  } catch { /* quota piena: la cache in memoria basta */ }
}

export function clearApiCache() {
  mem.clear();
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
    });
  } catch { /* ignore */ }
}

const _wait = (ms) => new Promise((res) => setTimeout(res, ms));
const _isNetworkError = (err) => !err.response;
// Errori del gateway (Cloudflare/ingress): il server si sta riavviando o è irraggiungibile
// per qualche secondo. Riprovare dopo una breve attesa quasi sempre risolve.
const _isGatewayError = (err) => [502, 503, 504, 520, 521, 522, 523, 524].includes(err.response?.status);
const _isRetriable = (err) => _isNetworkError(err) || _isGatewayError(err);

const rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
  // download di file e richieste esplicitamente non cachate passano dirette
  if (config.responseType || config.noCache) return rawGet(url, config);
  const key = url;
  const fetchNet = async (attempt = 0) => {
    try {
      const r = await rawGet(url, config);
      cacheSet(key, r.data);
      return r;
    } catch (err) {
      if (_isRetriable(err) && attempt < 3) {
        await _wait(1200 * (attempt + 1)); // il server può tornare in pochi secondi: riprova
        return fetchNet(attempt + 1);
      }
      if (_isRetriable(err)) {
        const c = cacheGet(key); // offline: meglio gli ultimi dati noti che una pagina vuota
        if (c) return { data: c.data, status: 200, headers: {}, config: { url }, fromCache: true };
      }
      throw err;
    }
  };
  const cached = cacheGet(key);
  if (cached && Date.now() - cached.t < FRESH_MS) {
    fetchNet().catch(() => {}); // revalida in background per la prossima visita
    return Promise.resolve({ data: cached.data, status: 200, headers: {}, config: { url }, fromCache: true });
  }
  return fetchNet();
};

// Ogni modifica (POST/PUT/DELETE/PATCH) invalida la cache: le GET successive sono fresche.
// Se il gateway risponde 5xx (server in riavvio), le azioni SICURE da ripetere vengono
// ritentate fino a 2 volte; mai quelle che spediscono qualcosa (email, WhatsApp, SMS).
const _NO_RETRY = /reply|compose|send|invia|sms|whatsapp|stripe|ai-|assistant|import|upload/i;
["post", "put", "delete", "patch"].forEach((m) => {
  const raw = api[m].bind(api);
  api[m] = async (...args) => {
    const url = String(args[0] || "");
    const canRetry = !_NO_RETRY.test(url);
    for (let attempt = 0; ; attempt++) {
      try {
        const r = await raw(...args);
        clearApiCache();
        return r;
      } catch (err) {
        if (canRetry && _isRetriable(err) && attempt < 2) {
          await _wait(1500 * (attempt + 1));
          continue;
        }
        throw err;
      }
    }
  };
});

// Se la sessione scade / manca, riporta al login
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem(TOKEN_KEY);
      clearApiCache();
      window.location.href = "/login?expired=1";
    }
    return Promise.reject(error);
  }
);

// Scarica il template Excel di esempio per "companies" o "persons".
export async function downloadTemplate(target) {
  const r = await api.get(`/import/template/${target}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = target === "companies" ? "template_aziende.xlsx" : "template_persone.xlsx";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default api;
